import { Callout, RadioBox, RadioGroup } from '@dev-spendesk/grapes';
import { useCallback, useEffect, useState } from 'react';

import { useTranslation } from 'src/core/common/hooks/useTranslation';

import { useAccountingSoftwareName } from '../../../apis';
import { isSyncMode, type SyncMode } from '../../../apis/useSetSyncSettings';

type Props = {
  value: SyncMode | undefined;
  fieldName?: string | undefined;
  onChange: (value: SyncMode) => void;
  currentlyMappedTo?: string;
  allowedSyncSettings?: SyncMode[];
};

export const SyncSettingsRadioList = ({
  value,
  fieldName,
  onChange,
  currentlyMappedTo,
  allowedSyncSettings,
}: Props) => {
  const { t } = useTranslation('global');
  const { accountingSoftwareName } = useAccountingSoftwareName();
  const [selectedSyncSetting, setSelectedSyncSetting] =
    useState<SyncMode | null>(null);

  useEffect(() => {
    if (value) {
      setSelectedSyncSetting(value);
    }
  }, [value]);

  const handleChange = useCallback((selectedValue: string) => {
    if (isSyncMode(selectedValue)) {
      setSelectedSyncSetting(selectedValue);
      onChange(selectedValue);
    }
  }, []);

  const isEnabled = useCallback(
    (mode: SyncMode) => {
      const allowList = allowedSyncSettings ?? [];
      if (!allowList.length) {
        return true;
      }

      return allowList.includes(mode);
    },
    [allowedSyncSettings],
  );

  const renderUl = (descriptions: string[] | unknown) => {
    if (Array.isArray(descriptions) === false) {
      return null;
    }
    return (
      <ul className="ml-16 list-disc">
        {descriptions.map((desc) => (
          <li key={desc}>{desc}</li>
        ))}
      </ul>
    );
  };
  return (
    <>
      <p className="text-content-primary heading-l mb-32 text-center">
        {t(
          'bookkeep.integrations.settings.analyticalFieldMapping.fullScreenModal.reviewSyncSettingsTitle',
          { fieldName, integration: accountingSoftwareName },
        )}
      </p>
      <RadioGroup
        name="syncSettingType"
        value={selectedSyncSetting}
        onChange={(event) => handleChange(event.target.value)}
        direction="column"
        className="mt-16 gap-16"
      >
        {isEnabled('pull') ? (
          <RadioBox
            iconName="arrow-uturn-left-down"
            iconVariant="blue"
            label={t(
              'bookkeep.integrations.settings.analyticalFieldMapping.syncSettings.pull.label',
              { integration: accountingSoftwareName },
            )}
            description={renderUl([
              t(
                'bookkeep.integrations.settings.analyticalFieldMapping.syncSettings.pull.descriptions.line1',
                { integration: accountingSoftwareName },
              ),
              t(
                'bookkeep.integrations.settings.analyticalFieldMapping.syncSettings.pull.descriptions.line2',
                { integration: accountingSoftwareName },
              ),
              t(
                'bookkeep.integrations.settings.analyticalFieldMapping.syncSettings.pull.descriptions.line3',
                { integration: accountingSoftwareName },
              ),
            ])}
            value="pull"
          />
        ) : (
          <></>
        )}

        {isEnabled('pullAndMap') ? (
          <RadioBox
            iconName="link"
            iconVariant="forest"
            label={t(
              'bookkeep.integrations.settings.analyticalFieldMapping.syncSettings.map.label',
              { integration: accountingSoftwareName },
            )}
            description={renderUl([
              t(
                'bookkeep.integrations.settings.analyticalFieldMapping.syncSettings.map.descriptions.line1',
                { integration: accountingSoftwareName },
              ),
              t(
                'bookkeep.integrations.settings.analyticalFieldMapping.syncSettings.map.descriptions.line2',
                { integration: accountingSoftwareName },
              ),
              t(
                'bookkeep.integrations.settings.analyticalFieldMapping.syncSettings.map.descriptions.line3',
                { integration: accountingSoftwareName },
              ),
            ])}
            value="pullAndMap"
          />
        ) : (
          <></>
        )}

        {isEnabled('none') ? (
          <RadioBox
            iconName="cross"
            iconVariant="apricot"
            label={t(
              'bookkeep.integrations.settings.analyticalFieldMapping.syncSettings.noSync.label',
            )}
            description={renderUl([
              t(
                'bookkeep.integrations.settings.analyticalFieldMapping.syncSettings.noSync.descriptions.line1',
                { integration: accountingSoftwareName },
              ),
              t(
                'bookkeep.integrations.settings.analyticalFieldMapping.syncSettings.noSync.descriptions.line2',
                { integration: accountingSoftwareName },
              ),
              t(
                'bookkeep.integrations.settings.analyticalFieldMapping.syncSettings.noSync.descriptions.line3',
                { integration: accountingSoftwareName },
              ),
            ])}
            value="none"
          />
        ) : (
          <></>
        )}
      </RadioGroup>
      {selectedSyncSetting === 'none' && Boolean(currentlyMappedTo) && (
        <div className="mt-8">
          <Callout
            variant="warning"
            title={t(
              'bookkeep.integrations.settings.analyticalFieldMapping.syncSettings.unMapWarning',
              {
                mapping: currentlyMappedTo,
                integration: accountingSoftwareName,
              },
            )}
          />
        </div>
      )}
    </>
  );
};
